/** @typedef {[number, number]} Point */
/** @typedef {import('./floorPlanDesignerState.js').DesignerState} DesignerState */

import { projectPointOntoSegment } from './floorPlanDesignerGeometry.js';
import {
  MIN_WALL_LENGTH_METERS,
  SPLIT_POINT_EPS_PX,
  getPointById,
  wallLengthMeters,
} from './floorPlanDesignerState.js';
import { screenToleranceToImagePx } from './floorPlanDesignerSnap.js';

/**
 * @param {Point} point
 * @param {DesignerState} state
 * @param {{
 *   snapTolerancePx?: number;
 *   displayScale?: number;
 *   pointSnapTolerancePx?: number;
 *   excludeWallIds?: string[];
 * }} [options]
 * @returns {{
 *   snapped: boolean;
 *   wallId?: string;
 *   point?: Point;
 *   t?: number;
 * }}
 */
export function findNearestWallLineSnap(point, state, options = {}) {
  const {
    snapTolerancePx = 12,
    displayScale = 1,
    pointSnapTolerancePx,
    excludeWallIds = [],
  } = options;

  const worldTolerance = screenToleranceToImagePx(snapTolerancePx, displayScale);
  const endpointTolerance =
    pointSnapTolerancePx != null
      ? pointSnapTolerancePx
      : worldTolerance;

  const exclude = new Set(excludeWallIds);
  let best = null;
  let bestDist = Infinity;

  for (const wall of state.walls) {
    if (exclude.has(wall.id)) continue;
    if (wallLengthMeters(state, wall) < MIN_WALL_LENGTH_METERS) continue;

    const startPt = getPointById(state, wall.startPointId);
    const endPt = getPointById(state, wall.endPointId);
    if (!startPt || !endPt) continue;

    const wallStart = [startPt.x, startPt.y];
    const wallEnd = [endPt.x, endPt.y];
    const { dist, t, projected } = projectPointOntoSegment(wallStart, wallEnd, point);

    if (dist > worldTolerance || dist >= bestDist) continue;

    const distStart = Math.hypot(projected[0] - startPt.x, projected[1] - startPt.y);
    const distEnd = Math.hypot(projected[0] - endPt.x, projected[1] - endPt.y);
    if (distStart <= endpointTolerance || distEnd <= endpointTolerance) {
      continue;
    }

    if (t <= 0 || t >= 1) continue;
    if (distStart < SPLIT_POINT_EPS_PX || distEnd < SPLIT_POINT_EPS_PX) continue;

    bestDist = dist;
    best = { wallId: wall.id, point: projected, t };
  }

  if (!best) {
    return { snapped: false };
  }

  return {
    snapped: true,
    wallId: best.wallId,
    point: best.point,
    t: best.t,
  };
}
